import { useState, useContext } from "react";
import { styled, Dialog, DialogContent, DialogTitle, DialogActions, Typography } from "@mui/material";
import { useTranslation, Trans } from "react-i18next";
import { StyledButton } from "../../../components/styled-button/styled-button";
import DeleteIcon from '@mui/icons-material/DeleteOutlineOutlined';
import { useService } from '../organizations-service'; 
import { OrganizationsContext } from "../organizations-context";
import pubSub, { EVENTS } from "../../../config/pubSub";

const DialogActionsContainer = styled(DialogActions)(({ theme }) => ({
  padding: theme.spacing(2),
  "& button": {
    margin: theme.spacing(1),
  },
}));

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
  paddingTop: theme.spacing(4),
  paddingLeft: theme.spacing(4),
  paddingRight: theme.spacing(4),
  paddingBottom: "0px",
  display: "flex"
}));

const StyledTypography = styled(Typography)(({ theme }) => ({
  color: "var(--kyocera-dark-grey)",
  fontFamily: "Noto Sans",
  fontSize: "36px",
  fontWeight: "bolder",
}));

const StyledDialogContent = styled(DialogContent)(({ theme }) => ({
  padding: theme.spacing(4),
  marginTop: "32px", 
}));

const DeleteOrganizationComponent = () => {
  const [showDialog, setShowDialog] = useState(false);
  const { t } = useTranslation();
  const { orgService } = useService();
  const { selectedCheckedOrgs, setSelectedOrg, setSelectedCheckedOrgs } = useContext(OrganizationsContext);
 
  const handleCancel = () => {
    setShowDialog(false);
  };

  const handleClick = () => {
    setShowDialog(true);
  };

  const handleClickOk = async () => {
    try {
        // Send delete requests for selected organizations
        await Promise.all(selectedCheckedOrgs.map(orgId => orgService.delete(orgId)));

        // After successful deletion
        if (selectedCheckedOrgs.length === 1) {
          pubSub.publish(EVENTS.SUCCESS, t("common_delete_single_org_success_message"));
        } else {
          pubSub.publish(EVENTS.SUCCESS, t("common_delete_mult_org_success_message"));
        }
        setSelectedCheckedOrgs([]); // Clear multiple selection
        setSelectedOrg(null); // Clear single selection
        pubSub.publish(EVENTS.ORGANIZATIONS_LIST); // Refresh list
    } catch (error) {
        console.error("Error deleting organizations:", error);
        // Handle error appropriately (e.g., show a notification)
    } finally {
        setShowDialog(false); // Close the dialog in the finally block
    }

};


  return (
    <>
      <StyledButton
        variant="contained"
        onClick={handleClick}
        className="d-flex align-items-center"
        id="delete-organization-button"
        startIcon={<DeleteIcon />}
        disabled={selectedCheckedOrgs.length === 0}
      >
        {t("cmp_organization_delete_button")}
      </StyledButton>
      <Dialog
        open={showDialog}
        maxWidth="sm"
        fullWidth
        onClose={(event, reason) => {
          if (reason !== "backdropClick") {
            handleCancel();
          }
        }}
        disableEscapeKeyDown
      >
        <StyledDialogTitle id="form-dialog-title">
          <StyledTypography>{t("cmp_organization_delete_title")}</StyledTypography>
        </StyledDialogTitle>
        <StyledDialogContent>
          {selectedCheckedOrgs.length === 1 ? (
            <Trans i18nKey="cmp_organization_delete_org_label" components={{ strong: <strong /> }} values={{ orgName: selectedCheckedOrgs[0] }} />
          ) : (
              <Trans i18nKey="cmp_organization_delete_org_label_mult"  components={{ strong: <strong /> }} values={{ itemCount: selectedCheckedOrgs.length }} />
          )}
        </StyledDialogContent>
        <DialogActionsContainer>
          <StyledButton onClick={handleCancel} variant="contained">
            {t("common_cancel")}
          </StyledButton>
          <StyledButton
            onClick={handleClickOk}
            variant="contained"
            color="primary"
            id="button-delete-org-submit"
          >
            {t("common_delete")}
          </StyledButton>
        </DialogActionsContainer>
      </Dialog>
    </>
  );
};

export default DeleteOrganizationComponent;
